import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import Select from "../components/Select";

const API = process.env.REACT_APP_API_URL;
const STORAGE_KEY = "data_trade_adjustment_page_v1";

function readStored() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch {
    return null;
  }
}

function writeStored(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* ignore */ }
}

export default function DataTradeAdjustmentPage() {
  const { accessToken, csrfToken, logout, selectedScenathon, user } = useAuth();
  const stored = useMemo(() => readStored(), []);

  // Determine user role
  const isAdmin = user?.type_id === 1;
  const userScenathonId = user?.scenathon_id;
  const userCountry = user?.country;

  const [loadingScenathons, setLoadingScenathons] = useState(true);
  const [scenathons, setScenathons] = useState([]);

  const [scenathonId, setScenathonId] = useState(
    selectedScenathon?.id || (isAdmin ? stored?.scenathonId : userScenathonId) || "",
  );
  const [pathwayId, setPathwayId] = useState(stored?.pathwayId || "");

  const [loadingPathways, setLoadingPathways] = useState(false);
  const [pathways, setPathways] = useState([]);

  const [countryId, setCountryId] = useState(
    isAdmin ? stored?.countryId : userCountry || ""
  );
  const [countries, setCountries] = useState([]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  // API fetch wrapper with CSRF token support
  const apiFetch = async (url, opts = {}) => {
    const headers = {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
      ...(opts.headers || {}),
    };
    const res = await fetch(url, { credentials: "include", ...opts, headers });
    if (res.status === 401) { logout?.(); throw new Error("Session expired"); }
    return res;
  };

  const handleRefresh = async () => {
    const controller = new AbortController();
    loadData({ signal: controller.signal }).then(() => {
      if (!controller.signal.aborted) {
        // success
      }
    });
  };

  // Persist selections to localStorage (only persist for admin users)
  useEffect(() => {
    writeStored({
      scenathonId: isAdmin ? scenathonId : "",
      pathwayId,
      countryId: isAdmin ? countryId : ""
    });
  }, [scenathonId, pathwayId, countryId, isAdmin]);

  // Load scenathons on mount
  useEffect(() => {
    let alive = true;
    setLoadingScenathons(true);

    apiFetch(`${API}/api/scenathon/scenathons`)
      .then((r) => r.json())
      .then((d) => {
        if (!alive) return;
        const opts = Array.isArray(d.scenathons) ? d.scenathons.map((s) => ({ value: s.id, label: s.name })) : [];
        setScenathons(opts);
      })
      .catch((e) => { console.error(e); if (alive) setScenathons([]); })
      .finally(() => { if (alive) setLoadingScenathons(false); });
    return () => { alive = false; };
  }, []);

  // Load pathways when scenathon changes
  useEffect(() => {
    if (!scenathonId) { setPathways([]); setPathwayId(""); return; }
    setLoadingPathways(true);
    let alive = true;

    const isAllowedPathway = (pathway) => {
      if (!pathway || typeof pathway !== "object") return false;
      const raw = String(pathway.label || pathway.value || pathway.pathway_name || "");
      return !raw.toLowerCase().includes("- adjusted");
    };

    apiFetch(`${API}/api/scenathon/available-pathways/${scenathonId}`)
      .then((r) => r.json())
      .then((d) => {
        if (!alive) return;
        const nextPathways = Array.isArray(d.pathways) ? d.pathways : [];
        const filteredPathways = nextPathways.filter(isAllowedPathway);
        setPathways(filteredPathways);
        setPathwayId((prev) => (
          filteredPathways.some((pathway) => String(pathway.value) === String(prev)) ? prev : ""
        ));
      })
      .catch((e) => { console.error(e); if (alive) setPathways([]); })
      .finally(() => { if (alive) setLoadingPathways(false); });
    return () => { alive = false; };
  }, [scenathonId]);

  useEffect(() => {
    if (selectedScenathon?.id && selectedScenathon.id !== scenathonId) {
      setScenathonId(String(selectedScenathon.id));
    }
  }, [selectedScenathon?.id, scenathonId]);

  // Reset table on selection change
  useEffect(() => { setRows([]); setError(""); if (isAdmin) setCountryId(""); setCountries([]); }, [scenathonId, pathwayId, isAdmin]);

  // Extract unique countries from loaded data
  useEffect(() => {
    if (rows.length === 0) {
      setCountries([]);
      return;
    }

    const uniqueCountries = Array.from(new Set(rows.map((r) => r.country).filter(Boolean)));
    const countryOptions = uniqueCountries.map((c) => ({ value: c, label: c })).sort((a, b) => a.label.localeCompare(b.label));
    setCountries(countryOptions);
    
    // For non-admin users, auto-set country to userCountry if available
    if (!isAdmin && userCountry) {
      setCountryId(userCountry);
    }
  }, [rows, isAdmin, userCountry]);

  const loadData = async ({ signal } = {}) => {
    if (!scenathonId || !pathwayId) return;

    setLoading(true);
    setRows([]);
    setError("");

    // Find the pathway label from the pathways array
    const selectedPathway = pathways.find((p) => String(p.value) === String(pathwayId));
    const pathwayName = selectedPathway?.label || String(pathwayId);

    const params = new URLSearchParams({ scenathonId: String(scenathonId), pathway: pathwayName });
    
    console.log("=== DataTradeAdjustment loadData ===");
    console.log("scenathonId:", scenathonId);
    console.log("pathwayId (value):", pathwayId);
    console.log("selectedPathway:", selectedPathway);
    console.log("pathwayName (label):", pathwayName);
    console.log("params.toString():", params.toString());
    console.log("Full URL:", `${API}/api/tradeadjustment/adjusted-data?${params}`);

    try {
      const res = await apiFetch(`${API}/api/tradeadjustment/adjusted-data?${params}`, { signal });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.error || `HTTP ${res.status}`);
      setRows(d.rows || []);
    } catch (e) {
      if (e.name === "AbortError") return;
      console.error(e);
      setError(e.message || "Could not load data.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when both selected
  useEffect(() => {
    if (!scenathonId || !pathwayId) return;
    let alive = true;
    const controller = new AbortController();

    loadData({ signal: controller.signal }).then(() => {
      if (!alive) return;
    });
    return () => { alive = false; };
  }, [scenathonId, pathwayId]);

  const handleRefreshTable = async () => {
    await loadData();
  };

  // Calculate selected pathway name for display
  const selectedPathwayName = useMemo(() => {
    if (!pathwayId || pathways.length === 0) return "";
    const selected = pathways.find((p) => String(p.value) === String(pathwayId));
    return selected?.label || String(pathwayId);
  }, [pathwayId, pathways]);

  // Filter rows by country if selected
  const filteredRows = countryId ? rows.filter((r) => r.country === countryId) : rows;

  const handleCopyTable = async () => {
    if (filteredRows.length === 0) return;

    // Create CSV content (without headers)
    const csvContent = filteredRows
      .map((row) =>
        [
          selectedPathwayName || "",
          row.product || "",
          row.year ?? "",
          row.imports != null ? row.imports : "",
          row.exports != null ? row.exports : "",
        ].join("\t")
      )
      .join("\n");

    try {
      await navigator.clipboard.writeText(csvContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div className="mt-9">
      <main className="py-10 px-6 min-h-screen mx-auto">
        <div className="space-y-8">
          {/* Selection Controls Card */}
          <div className="rounded-2xl bg-white shadow-sm border border-slate-100 hover:shadow-md transition-shadow p-8">
            {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-1 h-8 bg-gradient-to-b from-[#54B046] to-blue-500 rounded-full"></div>
            <h1 className="text-4xl font-bold text-slate-900">Adjusted Trade Values</h1>
          </div>
          <p className="text-slate-600 text-base ml-4">View and analyze trade data adjustments for your scenathons</p>
        </div>
            <h2 className="mb-6 text-xl font-semibold text-slate-900 flex items-center gap-2">
              <svg className="w-5 h-5 text-[#54B046]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
              Filter Data
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Select
                  label="Scenathon"
                  value={scenathonId}
                  onChange={isAdmin ? setScenathonId : undefined}
                  options={scenathons}
                  placeholder={loadingScenathons ? "Loading..." : "Select scenathon"}
                  disabled={loadingScenathons || !isAdmin}
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <Select
                  label="Pathway"
                  value={pathwayId}
                  onChange={setPathwayId}
                  options={pathways}
                  placeholder={
                    scenathonId
                      ? loadingPathways ? "Loading pathways..." : "Select pathway"
                      : "Select scenathon first"
                  }
                  disabled={!scenathonId || loadingPathways}
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <Select
                  label="Country"
                  value={countryId}
                  onChange={isAdmin ? setCountryId : undefined}
                  options={countries}
                  placeholder={countries.length > 0 ? "Select country (optional)" : "No countries"}
                  disabled={countries.length === 0 || !isAdmin}
                  clearable={isAdmin}
                  className="w-full"
                />
              </div>
            </div>
          </div>

          {/* Data Table Section */}
          <div className="rounded-2xl bg-white shadow-sm border border-slate-100 overflow-hidden">
            {!scenathonId || !pathwayId ? (
              <div className="p-16 text-center">
                <svg className="w-16 h-16 text-slate-300 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
                <p className="text-slate-600 text-lg font-medium">Select a scenathon and pathway</p>
                <p className="text-slate-500 text-sm mt-2">Choose options from the filter panel above to view adjusted trade data</p>
              </div>
            ) : loading ? (
              <div className="p-16 text-center">
                <div className="flex justify-center mb-4">
                  <svg className="animate-spin h-12 w-12 text-[#54B046]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                <p className="text-slate-600 text-lg font-medium">Loading data...</p>
              </div>
            ) : error ? (
              <div className="p-6 bg-gradient-to-r from-rose-50 to-pink-50 border-l-4 border-rose-400 m-4 rounded-lg">
                <div className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-rose-600 flex-shrink-0 mt-0.5" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                  </svg>
                  <div>
                    <p className="text-rose-800 font-semibold">Error loading data</p>
                    <p className="text-rose-700 text-sm mt-1">{error}</p>
                  </div>
                </div>
              </div>
            ) : rows.length === 0 ? (
              <div className="p-16 text-center">
                <svg className="w-16 h-16 text-slate-300 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-slate-600 text-lg font-medium">No data available</p>
                <p className="text-slate-500 text-sm mt-2">No adjusted trade values found for this combination</p>
              </div>
            ) : (
              <div className="p-6">
                {/* Header with row count and action buttons */}
                <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    
                    <p className="text-slate-600">
                      <span className="inline-block px-4 py-2 bg-gradient-to-r from-[#54B046] to-emerald-500 text-white rounded-full text-sm font-semibold">{filteredRows.length}</span> {countryId ? "matching" : "total"} records
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={handleCopyTable}
                      disabled={filteredRows.length === 0}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white border-2 border-slate-200 hover:border-[#54B046] text-slate-700 font-medium transition-all hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-slate-200 group"
                    >
                      <svg className="h-4 w-4 group-hover:text-[#54B046] transition-colors" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                      </svg>
                      {copied ? "Copied!" : "Copy"}
                    </button>

                    <button
                      onClick={handleRefreshTable}
                      disabled={!scenathonId || !pathwayId || loading}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-[#54B046] to-emerald-500 hover:shadow-lg text-white font-medium transition-all disabled:cursor-not-allowed disabled:opacity-50 group"
                    >
                      <svg className={`h-4 w-4 transition-transform ${loading ? 'animate-spin' : 'group-hover:rotate-180'} duration-300`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                      </svg>
                      {loading ? "Refreshing..." : "Refresh"}
                    </button>
                  </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
                        <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-700">Pathway</th>
                        <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-700">Product</th>
                        <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-700">Year</th>
                        <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-700">Imports</th>
                        <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-700">Exports</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {filteredRows.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="px-6 py-10 text-center text-slate-500">
                            No rows match the selected filters.
                          </td>
                        </tr>
                      ) : (
                        filteredRows.map((row, i) => (
                          <tr
                            key={i}
                            className={`hover:bg-blue-50/60 transition-colors ${
                              i % 2 === 0 ? "bg-white" : "bg-slate-50/40"
                            }`}
                          >
                            <td className="px-6 py-3 font-semibold text-slate-900">{selectedPathwayName || "—"}</td>
                            <td className="px-6 py-3 text-slate-700">{row.product || "—"}</td>
                            <td className="px-6 py-3 text-slate-700 font-medium">{row.year ?? "—"}</td>
                            <td className="px-6 py-3 text-right text-slate-700 font-medium">{row.imports != null ? Number(row.imports).toLocaleString() : "—"}</td>
                            <td className="px-6 py-3 text-right text-slate-700 font-medium">{row.exports != null ? Number(row.exports).toLocaleString() : "—"}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
